import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import InviteUserTable from "@/components/tables/InviteUserTable";
import { DashboardStatSkeleton } from "@/components/skeletons";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { fetchStats } from "@/services/helper.service";
import { BUTTONS, STATS } from "@/utils/constants/appConstants/commonConstants";
import { WELCOME_MESSAGE } from "@/utils/constants/messages";
import { getName } from "@/utils/functions/TokenFunction";
import { isFeaturePermitted, FEATURES_LOADED_EVENT } from "@/utils/functions/rbac";
import { NOTIFICATION_EVENTS } from "@/utils/constants/constants";
import { useNotificationTrigger } from "@/utils/hooks/useNotificationTrigger";
import { useAuth } from "@/context/providers/AuthProvider";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

type StatCardProps = {
    id: string;
    countId: string;
    textId: string;
    value: number;
    label: string;
};

function StatCard({ id, countId, textId, value, label }: StatCardProps) {
    return (
        <div className="gs-dashboard-stat-col">
            <div className="gs-dashboard-stat" id={id}>
                <div className="gs-dashboard-stat-value bold-Text" id={countId}>
                    {value}
                </div>
                <div className="gs-dashboard-stat-label normal-Text" id={textId}>
                    {label}
                </div>
            </div>
        </div>
    );
}

const defaultStats = {
    admins: 0,
    users: 0,
    envelopes: 0,
    documents: 0,
};

const Dashboard = () => {
    const { organization } = useContext(OrganizationContext);
    const statsBootstrap = getListCacheBootstrap("dashboard-stats", { organizationId: organization?.id });

    const [isInviteUserPermitted, setIsInviteUserPermitted] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(!statsBootstrap.hasCache);
    const [stats, setStats] = useState(
        statsBootstrap.hasCache
            ? (statsBootstrap.items[0] as typeof defaultStats)
            : defaultStats
    );

    useEffect(() => {
        const refreshPermissions = () => {
            setIsInviteUserPermitted(isFeaturePermitted("AdminPortalCreateUser"));
        };

        refreshPermissions();
        window.addEventListener(FEATURES_LOADED_EVENT, refreshPermissions);
        return () => window.removeEventListener(FEATURES_LOADED_EVENT, refreshPermissions);
    }, []);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("dashboard-stats", { organizationId: organization?.id });

        if (!cached) {
            setIsStatsLoading(true);
        }

        fetchStats(
            (response: any) => {
                writeListCache(cacheKey, [response], 1);
                setStats(response);
                setIsStatsLoading(false);
            },
            (error: any) => {
                setIsStatsLoading(false);
                console.log(error);
            }
        );
    }, [organization]);

    const { logOut } = useAuth();
    useNotificationTrigger(NOTIFICATION_EVENTS.REFRESH_LOGOUT, async () => {
        await logOut();
    });

    const name = getName();

    return (
        <div className="gs-dashboard-page">
            <div className="gs-dashboard-header">
                <div className="gs-dashboard-header-content">
                    <h3
                        className="gs-dashboard-title page-title text-capitalize"
                        id="dashboard-greeting-meesage"
                    >
                        Hello, {name ? name : "Loading"}!
                    </h3>
                    <div className="gs-dashboard-subtitle-wrap text-muted-foreground">
                        <p id="dashboard-welcome-message">{WELCOME_MESSAGE}</p>
                    </div>
                </div>
                {isInviteUserPermitted && (
                    <div className="gs-dashboard-header-actions">
                        <Button asChild className="gs-dashboard-invite-btn">
                            <Link to="/invite-users" id="dashboard-invite-btn">
                                <em
                                    className="icon ni ni-users"
                                    id="dashboard-invite-btn-icon"
                                />
                                <span className="gs-dashboard-invite-btn-label">
                                    {BUTTONS.INVITE_USERS}
                                </span>
                            </Link>
                        </Button>
                    </div>
                )}
            </div>

            <div className="gs-dashboard-stats">
                {isStatsLoading ? (
                    <DashboardStatSkeleton />
                ) : (
                    <>
                        <StatCard
                            id="dashboard-admin-stats"
                            countId="dashboard-admin-stats-count"
                            textId="dashboard-admin-stats-text"
                            value={stats?.admins}
                            label={STATS.ADMINS}
                        />
                        <StatCard
                            id="dashboard-user-stats"
                            countId="dashboard-user-stats-count"
                            textId="dashboard-user-stats-text"
                            value={stats?.users}
                            label={STATS.USERS}
                        />
                        <StatCard
                            id="dashboard-envelope-stats"
                            countId="dashboard-envelope-stats-count"
                            textId="dashboard-envelope-stats-text"
                            value={stats?.envelopes}
                            label={STATS.ENVELOPES}
                        />
                        <StatCard
                            id="dashboard-document-stats"
                            countId="dashboard-document-stats-count"
                            textId="dashboard-document-stats-text"
                            value={stats?.documents}
                            label={STATS.DOCUMENTS}
                        />
                    </>
                )}
            </div>

            <InviteUserTable />
        </div>
    );
};

export default Dashboard;
